import {
  buildProfilePictureUrlForMode,
  dispatchProfileUpdated,
  isPlaceholderProfilePictureUrl,
} from '../MainHeader/profilePictureUrl';
import { clearDiscussionFeedSessionCaches } from '../support/openDiscussions/cache/discussionFeedCache';

const STORAGE_KEY = 'bahlus_active_profile_mode';

export const PROFILE_MODES = {
  PERSONAL: 'personal',
  SOCIAL: 'social',
};

export function normalizeProfileMode(mode) {
  return String(mode || PROFILE_MODES.PERSONAL).trim().toLowerCase() === PROFILE_MODES.SOCIAL
    ? PROFILE_MODES.SOCIAL
    : PROFILE_MODES.PERSONAL;
}

export function getStoredActiveProfileMode() {
  try {
    return normalizeProfileMode(localStorage.getItem(STORAGE_KEY));
  } catch {
    return PROFILE_MODES.PERSONAL;
  }
}

export function setStoredActiveProfileMode(mode) {
  try {
    localStorage.setItem(STORAGE_KEY, normalizeProfileMode(mode));
  } catch {
    // ignore
  }
}

/** Persist active mode, drop stale feed caches, and notify listeners to reload UI. */
export function commitActiveProfileModeSwitch(mode) {
  const next = normalizeProfileMode(mode);
  setStoredActiveProfileMode(next);
  clearDiscussionFeedSessionCaches();
  dispatchProfileUpdated();
  return next;
}

/** Viewer follow identity: prefer stored mode when it disagrees with cached profile (stale API). */
export function resolveViewerFollowMode(viewerProfile) {
  const stored = getStoredActiveProfileMode();
  if (viewerProfile?.profileMode && normalizeProfileMode(viewerProfile.profileMode) === stored) {
    return normalizeProfileMode(viewerProfile.profileMode);
  }
  return stored;
}

export function resolveActiveProfileMode(userProfile, profileIdentity) {
  const fromIdentity = profileIdentity?.activeProfileMode;
  const fromUser = userProfile?.active_profile_mode;
  const stored = getStoredActiveProfileMode();
  const candidate = fromIdentity || fromUser || stored;
  if (candidate === PROFILE_MODES.SOCIAL && profileIdentity?.social?.enabled) {
    return PROFILE_MODES.SOCIAL;
  }
  return PROFILE_MODES.PERSONAL;
}

export function buildActiveProfilePicturePreview(
  userProfile,
  profileIdentity,
  baseUrl = process.env.REACT_APP_BASE_URL || ''
) {
  const mode = resolveActiveProfileMode(userProfile, profileIdentity);
  const view = activeProfileView(userProfile, profileIdentity, mode);
  return buildProfilePictureUrlForMode(view.profilePictureUrl, baseUrl, mode, {
    userId: userProfile?.userId,
    name: view.displayName,
  });
}

export function hasActiveProfilePicture(userProfile, profileIdentity) {
  const view = activeProfileView(userProfile, profileIdentity);
  return !isPlaceholderProfilePictureUrl(view.profilePictureUrl);
}

export function activeProfileView(userProfile, profileIdentity, mode) {
  const resolved = normalizeProfileMode(mode || resolveActiveProfileMode(userProfile, profileIdentity));
  if (resolved === PROFILE_MODES.SOCIAL && profileIdentity?.social?.enabled) {
    return {
      mode: PROFILE_MODES.SOCIAL,
      handle: profileIdentity.social.handle || '',
      displayName: profileIdentity.social.displayName || profileIdentity.social.handle || 'You',
      profilePictureUrl: profileIdentity.social.profilePictureUrl || '',
      coverPictureUrl: profileIdentity.social.coverPictureUrl || '',
      bio: profileIdentity.social.bio || '',
      enabled: true,
    };
  }
  const first = userProfile?.first_name || '';
  const last = userProfile?.last_name || '';
  return {
    mode: PROFILE_MODES.PERSONAL,
    handle: profileIdentity?.personal?.handle || '',
    displayName: profileIdentity?.personal?.displayName || [first, last].filter(Boolean).join(' ') || userProfile?.email || 'You',
    profilePictureUrl: profileIdentity?.personal?.profilePictureUrl || userProfile?.profile_picture_url || '',
    bio: profileIdentity?.personal?.bio || userProfile?.bio || '',
    enabled: true,
  };
}

export const PROFILE_IDENTITY_NOTE = {
  title: 'Personal profile vs Social profile',
  personal:
    'Your personal profile is for people you know — close acquaintances, colleagues, and connections you are aware of. Posts, comments, followers, and activity stay separate from your social profile.',
  social:
    'Your social profile is optional. Use it for the wider community — questions, views, and open discussion with anyone. It has its own handle, display name, picture, followers, and activity. Nothing overlaps with your personal profile.',
};
